import { useContext, useEffect } from 'react';
import { OptimizelyContext, OptimizelyDecideOption } from '@optimizely/react-sdk';
import { getOrCreateUserId } from '../provider/user-id';
import { EXPERIMENT_FLAG_KEYS } from '../experiment-registry';
import { Condition, evaluateCondition } from '../conditions';

interface ClickEventRule {
	selector: string;
	eventKey: string;
}

interface ClickPage {
	conditions?: Condition;
	rules: ClickEventRule[];
}

export function useExperimentClickTracking() {
	const { optimizely } = useContext(OptimizelyContext);

	useEffect(() => {
		if (!optimizely) return;

		const controller = new AbortController();
		let removeListener: (() => void) | null = null;

		optimizely.onReady().then(async () => {
			if (controller.signal.aborted) return;

			const userId = getOrCreateUserId() ?? undefined;
			const allRules: ClickEventRule[] = [];

			for (const flagKey of EXPERIMENT_FLAG_KEYS) {
				if (controller.signal.aborted) return;

				const decision = optimizely.decide(
					flagKey,
					[OptimizelyDecideOption.DISABLE_DECISION_EVENT],
					userId
				);
				const variable = (decision?.variables?.['click_events'] ?? {}) as {
					pages?: ClickPage[];
				};

				for (const page of variable.pages ?? []) {
					if (controller.signal.aborted) return;
					const matches =
						!page.conditions || (await evaluateCondition(page.conditions, controller.signal));
					if (matches) {
						allRules.push(...page.rules);
					}
				}
			}

			if (!allRules.length || controller.signal.aborted) return;

			const handler = (e: Event) => {
				const target = e.target as HTMLElement;
				const fired = new Set<string>();
				for (const { selector, eventKey } of allRules) {
					if (!fired.has(eventKey) && target.closest(selector)) {
						// Call optimizely.track() directly — the handler is only registered after
						// onReady() resolves, so the client is guaranteed to be ready.
						optimizely.track(eventKey, userId);
						fired.add(eventKey);
					}
				}
			};

			document.addEventListener('click', handler, { capture: true });
			removeListener = () => document.removeEventListener('click', handler, { capture: true });

			// Guard against the race where cleanup fired between the last abort check and here.
			if (controller.signal.aborted) {
				removeListener();
				removeListener = null;
			}
		});

		return () => {
			controller.abort();
			removeListener?.();
		};
	}, [optimizely]);
}
